import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import ContractDocsAlert from '../components/ContractDocsAlert';
import ContractDocumentsPreview from '../components/ContractDocumentsPreview';
import ContractFirmaCard from '../components/ContractFirmaCard';
import ContractRicontattoCard from '../components/ContractRicontattoCard';
import PodTimelineCard from '../components/PodTimelineCard';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Zap,
  Flame,
  User,
  Building,
  Euro,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Wifi,
  Mail,
  MessageSquare,
  FileText,
  MapPin,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Octopus Energy Trade Link per inserimento diretto contratti
const OCTOPUS_TRADE_LINK = "https://clk.tradedoubler.com/click?p=357545&a=3366236";

const STATI_PRATICA = [
  { value: 'bozza', label: 'Bozza' },
  { value: 'documenti_richiesti', label: 'Documenti richiesti' },
  { value: 'documenti_ricevuti', label: 'Documenti ricevuti' },
  { value: 'in_lavorazione', label: 'In lavorazione' },
  { value: 'inviata', label: 'Inviata' },
  { value: 'inserita_portale', label: 'Inserita nel portale' },
  { value: 'accettata', label: 'Accettata' },
  { value: 'attiva', label: 'Attiva' },
  { value: 'pagabile', label: 'Pagabile' },
  { value: 'scartata', label: 'Scartata' },
  { value: 'annullata', label: 'Annullata' },
];

const METODI_PAGAMENTO = [
  { value: 'bolletta_web', label: 'Bolletta Web' },
  { value: 'bollettino', label: 'Bollettino postale' },
  { value: 'rid', label: 'RID / Domiciliazione bancaria' },
  { value: 'bolletta_cartacea', label: 'Bolletta cartacea' },
];

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, user: currentUser } = useAuth();
  
  const [contract, setContract] = useState(null);
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    fetchContract();
    fetchUsers();
    fetchNotes();
    fetchActivities();
    fetchCompanies();
  }, [id]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies?attiva=true`);
      setCompanies(response.data);
    } catch (error) {}
  };

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contracts/${id}`);
      setContract(response.data);
      setEditData(response.data);
      
      // Fetch client details
      if (response.data.cliente_id) {
        const clientResponse = await axios.get(`${API_URL}/api/clients/${response.data.cliente_id}`);
        setClient(clientResponse.data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento della pratica');
      navigate('/contracts');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.filter(u => ['sub_agent', 'admin', 'super_admin'].includes(u.ruolo)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notes/contract/${id}`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/activities/contract/${id}`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleSave = async () => {
    try {
      // Coerce empty strings to null for numeric fields (Pydantic rejects empty strings on int/float).
      const toNum = (v) => (v === '' || v === null || v === undefined ? null : (Number.isNaN(parseFloat(v)) ? null : parseFloat(v)));
      const toInt = (v) => (v === '' || v === null || v === undefined ? null : (Number.isNaN(parseInt(v, 10)) ? null : parseInt(v, 10)));

      const payload = {
        ...editData,
        commissione_prevista: toNum(editData.commissione_prevista),
        commissione_maturata: toNum(editData.commissione_maturata),
        commissione_agenzia: toNum(editData.commissione_agenzia),
        gettone: toNum(editData.gettone),
        velocita_download: toInt(editData.velocita_download),
        velocita_upload: toInt(editData.velocita_upload),
        prezzo_mensile: toNum(editData.prezzo_mensile),
        costo_attivazione: toNum(editData.costo_attivazione),
      };
      await axios.put(`${API_URL}/api/contracts/${id}`, payload);
      toast.success('Pratica aggiornata');
      setEditing(false);
      fetchContract();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API_URL}/api/notes`, {
        entita_tipo: 'contract',
        entita_id: id,
        contenuto: newNote
      });
      setNewNote('');
      fetchNotes();
      fetchActivities();
      toast.success('Nota aggiunta');
    } catch (error) {
      toast.error('Errore nell\'aggiunta della nota');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const getStatoColor = (stato) => {
    const colors = {
      bozza: 'bg-slate-100 text-slate-700',
      documenti_richiesti: 'bg-amber-100 text-amber-700',
      documenti_ricevuti: 'bg-sky-100 text-sky-700',
      in_lavorazione: 'bg-violet-100 text-violet-700',
      inviata: 'bg-indigo-100 text-indigo-700',
      accettata: 'bg-emerald-100 text-emerald-700',
      attiva: 'bg-green-100 text-green-700',
      scartata: 'bg-red-100 text-red-700',
      annullata: 'bg-red-100 text-red-700'
    };
    return colors[stato] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contract-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/contracts')} className="shrink-0">
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Indietro</span>
          </Button>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {contract?.tipologia === 'luce' ? (
              <div className="p-2 rounded-lg bg-amber-100 shrink-0">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
            ) : contract?.tipologia === 'gas' ? (
              <div className="p-2 rounded-lg bg-sky-100 shrink-0">
                <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-sky-600" />
              </div>
            ) : (
              <div className="p-2 rounded-lg bg-violet-100 flex shrink-0">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
                <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-sky-500 -ml-1" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">
                Pratica {contract?.tipologia?.toUpperCase()}
              </h1>
              <p className="text-slate-500 flex items-center gap-1.5 flex-wrap text-xs sm:text-sm">
                {contract?.codice_pratica_interno && (
                  <span className="font-mono font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-md text-[10px] sm:text-xs" data-testid="codice-pratica-interno">
                    {contract.codice_pratica_interno}
                  </span>
                )}
                {contract?.codice_risposta_gestore && (
                  <span className="font-mono text-[10px] sm:text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md" data-testid="codice-risposta-gestore" title="Codice risposta gestore">
                    Gestore: {contract.codice_risposta_gestore}
                  </span>
                )}
                <span className="text-[10px] sm:text-xs text-slate-400">#{contract?.id?.slice(0, 8)}</span>
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </>
          ) : (
            <>
              {(() => {
                const LOCKED = ['in_lavorazione', 'inviata', 'inserita_portale', 'accettata', 'attiva', 'pagabile'];
                const isSubAgent = currentUser?.ruolo === 'sub_agent';
                const isLocked = isSubAgent && LOCKED.includes(contract?.stato);
                if (isLocked) {
                  return (
                    <span className="text-sm text-slate-500 flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-xl px-3 py-1.5" data-testid="pratica-lock-badge">
                      <FileText className="w-4 h-4" /> Pratica bloccata dall'admin — solo lettura
                    </span>
                  );
                }
                return (
                  <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-contract-btn">
                    <Edit className="w-4 h-4 mr-2" />
                    Modifica
                  </Button>
                );
              })()}
              {hasRole(['super_admin', 'back_office', 'interno']) && ['in_lavorazione','inviata','inserita_portale','accettata','attiva','pagabile','scartata','annullata'].includes(contract?.stato) && (
                <Button
                  variant="outline"
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={async () => {
                    if (!window.confirm("Riaprire la pratica per documenti mancanti? Il sub-agente potra' modificarla nuovamente.")) return;
                    try {
                      await axios.post(`${API_URL}/api/contracts/${id}/riapri`);
                      toast.success('Pratica riaperta');
                      fetchContract();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Errore riapertura');
                    }
                  }}
                  data-testid="riapri-contract-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Riapri per documenti
                </Button>
              )}
              {contract?.stato === 'bozza' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="text-sky-700 border-sky-200 hover:bg-sky-50" data-testid="change-tipologia-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Cambia tipo
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Converti in</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {[
                      { v: 'luce', label: 'Luce' },
                      { v: 'gas', label: 'Gas' },
                      { v: 'dual', label: 'Dual (Luce + Gas)' },
                      { v: 'internet', label: 'Internet / Telefonia' },
                      { v: 'tv', label: 'TV' },
                      { v: 'tv_internet', label: 'TV + Internet' },
                    ].filter(o => o.v !== contract?.tipologia).map(o => (
                      <DropdownMenuItem
                        key={o.v}
                        onClick={async () => {
                          if (!window.confirm(`Convertire questa bozza in ${o.label}? I campi tecnici specifici verranno azzerati (fornitore, offerta, prezzi). Dati cliente e POD/PDR verranno mantenuti.`)) return;
                          try {
                            await axios.put(`${API_URL}/api/contracts/${id}/change-tipologia`, { tipologia: o.v });
                            toast.success(`Convertito in ${o.label}`);
                            fetchContract();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Errore conversione');
                          }
                        }}
                        data-testid={`change-tipo-${o.v}`}
                      >
                        {o.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>
      </div>

      {/* Documenti mancanti / Alert richieste */}
      <ContractDocsAlert
        contractId={id}
        isAdmin={hasRole(['super_admin', 'back_office', 'interno'])}
        onDocRequested={fetchContract}
      />

      {/* Anteprima documenti caricati */}
      <ContractDocumentsPreview
        contractId={id}
        clientId={contract?.cliente_id}
        refreshKey={contract?.updated_at}
      />

      {/* Firma OTP cliente */}
      <ContractFirmaCard
        contractId={id}
        clientEmail={client?.email}
        clientTelefono={client?.telefono}
      />

      {/* Data ricontatto / Promemoria rinnovo */}
      <ContractRicontattoCard
        contractId={id}
        contract={contract}
        onSaved={fetchContract}
      />

      {/* Timeline POD / PDR per il codice fiscale del cliente */}
      {(contract?.pod || contract?.pdr) && contract?.cliente_codice_fiscale && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {contract.pod && (
            <PodTimelineCard
              codice={contract.pod}
              codiceFiscale={contract.cliente_codice_fiscale}
              tipo="pod"
              currentContractId={id}
            />
          )}
          {contract.pdr && (
            <PodTimelineCard
              codice={contract.pdr}
              codiceFiscale={contract.cliente_codice_fiscale}
              tipo="pdr"
              currentContractId={id}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contract Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dettagli Pratica</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipologia</Label>
                    <Select 
                      value={editData.tipologia} 
                      onValueChange={(v) => setEditData({...editData, tipologia: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="luce">Luce</SelectItem>
                        <SelectItem value="gas">Gas</SelectItem>
                        <SelectItem value="dual">Dual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(editData.tipologia === 'luce' || editData.tipologia === 'gas' || editData.tipologia === 'dual') && (
                    <div className="space-y-2">
                      <Label>Tipo Operazione</Label>
                      <Select
                        value={editData.tipo_operazione || 'none'}
                        onValueChange={(v) => setEditData({...editData, tipo_operazione: v === 'none' ? null : v})}
                      >
                        <SelectTrigger data-testid="edit-tipo-operazione">
                          <SelectValue placeholder="Non specificato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non specificato</SelectItem>
                          <SelectItem value="switch">Switch</SelectItem>
                          <SelectItem value="voltura_switch">Voltura con Switch</SelectItem>
                          <SelectItem value="posa_contatore">Posa Contatore</SelectItem>
                          <SelectItem value="subentro">Subentro</SelectItem>
                          <SelectItem value="nuovo_allaccio">Nuovo Allaccio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select 
                      value={editData.stato} 
                      onValueChange={(v) => setEditData({...editData, stato: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATI_PRATICA.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fornitore</Label>
                    <Select 
                      value={editData.fornitore || undefined} 
                      onValueChange={(v) => setEditData({...editData, fornitore: v, offerta: ''})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleziona fornitore" /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Offerta</Label>
                    {(() => {
                      const comp = companies.find(c => c.nome === editData.fornitore);
                      const offerte = comp?.offerte?.filter(o => o.attiva) || [];
                      return offerte.length > 0 ? (
                        <Select value={editData.offerta || undefined} onValueChange={(v) => setEditData({...editData, offerta: v})}>
                          <SelectTrigger><SelectValue placeholder="Seleziona offerta" /></SelectTrigger>
                          <SelectContent>
                            {offerte.map(o => <SelectItem key={o.id} value={o.nome}>{o.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={editData.offerta || ''} onChange={(e) => setEditData({...editData, offerta: e.target.value})} />
                      );
                    })()}
                  </div>
                  {hasRole(['super_admin', 'admin']) && (
                    <div className="space-y-2">
                      <Label>Agente Assegnato</Label>
                      <Select 
                        value={editData.sub_agente_id || "none"} 
                        onValueChange={(v) => setEditData({...editData, sub_agente_id: v === "none" ? "" : v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non assegnato</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {(editData.tipologia === 'luce' || editData.tipologia === 'dual') && (
                    <div className="space-y-2">
                      <Label>POD (Punto di Prelievo)</Label>
                      <Input
                        value={editData.pod || ''}
                        onChange={(e) => setEditData({...editData, pod: e.target.value})}
                        placeholder="IT001E12345678"
                      />
                    </div>
                  )}
                  {(editData.tipologia === 'gas' || editData.tipologia === 'dual') && (
                    <div className="space-y-2">
                      <Label>PDR (Punto di Riconsegna)</Label>
                      <Input
                        value={editData.pdr || ''}
                        onChange={(e) => setEditData({...editData, pdr: e.target.value})}
                        placeholder="12345678901234"
                      />
                    </div>
                  )}
                  {(editData.tipologia === 'luce' || editData.tipologia === 'gas' || editData.tipologia === 'dual') && (
                    <div className="space-y-2 col-span-full">
                      <Label className="flex items-center gap-1">
                        Indirizzo Fornitura
                        <span className="text-[10px] text-slate-400 font-normal">(via, civico, citta, cap)</span>
                      </Label>
                      <Input
                        value={editData.indirizzo_fornitura || ''}
                        onChange={(e) => setEditData({...editData, indirizzo_fornitura: e.target.value})}
                        placeholder="Via Roma 12, 20100 Milano (MI)"
                        data-testid="input-indirizzo-fornitura"
                      />
                    </div>
                  )}
                  {editData.tipologia === 'internet' && (
                    <div className="space-y-3 bg-violet-50 rounded-lg p-4 border border-violet-200">
                      <p className="text-sm font-semibold text-violet-700">Dettagli Internet/Fibra</p>
                      <div className="space-y-2">
                        <Label>Codice Cliente Internet</Label>
                        <Input value={editData.codice_cliente_internet || ''} onChange={(e) => setEditData({...editData, codice_cliente_internet: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Tecnologia</Label>
                          <Select value={editData.tecnologia || 'FTTH'} onValueChange={(v) => setEditData({...editData, tecnologia: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FTTH">FTTH</SelectItem>
                              <SelectItem value="FTTC">FTTC</SelectItem>
                              <SelectItem value="FWA">FWA</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Prezzo Mensile (EUR)</Label>
                          <Input type="number" step="0.01" value={editData.prezzo_mensile || ''} onChange={(e) => setEditData({...editData, prezzo_mensile: e.target.value})} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Download (Mbps)</Label>
                          <Input type="number" value={editData.velocita_download || ''} onChange={(e) => setEditData({...editData, velocita_download: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label>Upload (Mbps)</Label>
                          <Input type="number" value={editData.velocita_upload || ''} onChange={(e) => setEditData({...editData, velocita_upload: e.target.value})} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Costo Attivazione (EUR)</Label>
                          <Input type="number" step="0.01" value={editData.costo_attivazione || ''} onChange={(e) => setEditData({...editData, costo_attivazione: e.target.value})} />
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                          <input type="checkbox" checked={editData.modem_incluso || false} onChange={(e) => setEditData({...editData, modem_incluso: e.target.checked})} className="rounded border-slate-300" />
                          <Label>Modem Incluso</Label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Indirizzo Installazione</Label>
                        <Input value={editData.indirizzo_installazione || ''} onChange={(e) => setEditData({...editData, indirizzo_installazione: e.target.value})} />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Metodo Pagamento</Label>
                    <Select value={editData.metodo_pagamento || "rid"} onValueChange={(v) => setEditData({...editData, metodo_pagamento: v, iban: v !== 'rid' ? '' : editData.iban})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rid">RID Bancario</SelectItem>
                        <SelectItem value="bollettino">Bollettino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Bolletta</Label>
                    <Select value={editData.tipo_bolletta || "web"} onValueChange={(v) => setEditData({...editData, tipo_bolletta: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="web">Bolletta Web</SelectItem>
                        <SelectItem value="cartacea">Bolletta Cartacea</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(editData.metodo_pagamento === 'rid' || !editData.metodo_pagamento) && (
                    <div className="space-y-2">
                      <Label>IBAN</Label>
                      <Input
                        value={editData.iban || ''}
                        onChange={(e) => setEditData({...editData, iban: e.target.value.toUpperCase().replace(/\s/g, '')})}
                        placeholder="IT60X0542811101000000123456"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Data Attivazione</Label>
                    <Input
                      type="date"
                      value={editData.data_attivazione || ''}
                      onChange={(e) => setEditData({...editData, data_attivazione: e.target.value})}
                    />
                  </div>
                  {!hasRole(['interno', 'back_office']) && !hasRole(['sub_agent']) && (
                    <>
                      <div className="space-y-2">
                        <Label>Commissione Sub-Agente (EUR)</Label>
                        <Input
                          type="number"
                          value={editData.commissione_maturata || ''}
                          onChange={(e) => setEditData({...editData, commissione_maturata: e.target.value})}
                          placeholder="Importo che spetta al sub-agente"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Commissione Agenzia (EUR)</Label>
                        <Input
                          type="number"
                          value={editData.commissione_agenzia || ''}
                          onChange={(e) => setEditData({...editData, commissione_agenzia: e.target.value})}
                          placeholder="Profitto agenzia su questa pratica"
                        />
                      </div>
                    </>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label>Esito</Label>
                    <Input
                      value={editData.esito || ''}
                      onChange={(e) => setEditData({...editData, esito: e.target.value})}
                      placeholder="es. Contratto attivato con successo"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Note Operative</Label>
                    <textarea
                      className="w-full h-24 px-3 py-2 text-sm border rounded-md"
                      value={editData.note_operative || ''}
                      onChange={(e) => setEditData({...editData, note_operative: e.target.value})}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-500">Fornitore</p>
                      <p className="font-medium">{contract?.fornitore || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Offerta</p>
                      <p className="font-medium">{contract?.offerta || '-'}</p>
                    </div>
                    {contract?.tipo_operazione && (
                      <div>
                        <p className="text-xs text-slate-500">Tipo Operazione</p>
                        <p className="font-medium">
                          {{
                            'switch': 'Switch',
                            'voltura_switch': 'Voltura con Switch',
                            'posa_contatore': 'Posa Contatore',
                            'subentro': 'Subentro',
                            'nuovo_allaccio': 'Nuovo Allaccio'
                          }[contract.tipo_operazione] || contract.tipo_operazione}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-500">Canale Acquisizione</p>
                      <p className="font-medium capitalize">{contract?.canale_acquisizione?.replace('_', ' ') || '-'}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-500">Agente Assegnato</p>
                      <p className="font-medium">{contract?.sub_agente_nome || 'Non assegnato'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Data Attivazione</p>
                      <p className="font-medium">{contract?.data_attivazione || '-'}</p>
                    </div>
                    {contract?.esito && (
                      <div>
                        <p className="text-xs text-slate-500">Esito</p>
                        <p className="font-medium">{contract.esito}</p>
                      </div>
                    )}
                  </div>
                  {(contract?.pod || contract?.pdr) && (
                    <div className="col-span-2 pt-4 border-t">
                      <p className="text-xs text-slate-500 mb-2">Punti di Fornitura</p>
                      <div className="flex items-center gap-6 flex-wrap">
                        {contract?.pod && (
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-amber-500" />
                            <span className="text-sm font-medium">POD:</span>
                            <span className="font-mono text-sm">{contract.pod}</span>
                          </div>
                        )}
                        {contract?.pdr && (
                          <div className="flex items-center gap-2">
                            <Flame className="w-4 h-4 text-sky-500" />
                            <span className="text-sm font-medium">PDR:</span>
                            <span className="font-mono text-sm">{contract.pdr}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {contract?.indirizzo_fornitura && (contract?.tipologia === 'luce' || contract?.tipologia === 'gas' || contract?.tipologia === 'dual') && (
                    <div className="col-span-2 pt-4 border-t" data-testid="view-indirizzo-fornitura">
                      <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" /> Indirizzo Fornitura
                      </p>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{contract.indirizzo_fornitura}</p>
                    </div>
                  )}
                  {contract?.tipologia === 'internet' && (
                    <div className="col-span-2 pt-4 border-t">
                      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><Wifi className="w-3.5 h-3.5 text-violet-500" /> Dettagli Internet/Fibra</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        {contract?.codice_cliente_internet && <div><p className="text-xs text-slate-400">Codice Cliente</p><p className="font-mono text-sm">{contract.codice_cliente_internet}</p></div>}
                        {contract?.tecnologia && <div><p className="text-xs text-slate-400">Tecnologia</p><p className="font-medium">{contract.tecnologia}</p></div>}
                        {contract?.velocita_download && <div><p className="text-xs text-slate-400">Velocita'</p><p className="font-medium">{contract.velocita_download}↓ / {contract.velocita_upload || '?'}↑ Mbps</p></div>}
                        {contract?.prezzo_mensile && <div><p className="text-xs text-slate-400">Prezzo Mensile</p><p className="font-bold text-violet-700">{contract.prezzo_mensile.toFixed(2)} EUR/mese</p></div>}
                        {contract?.costo_attivazione > 0 && <div><p className="text-xs text-slate-400">Attivazione</p><p className="font-medium">{contract.costo_attivazione.toFixed(2)} EUR</p></div>}
                        <div><p className="text-xs text-slate-400">Modem</p><p className="font-medium">{contract?.modem_incluso ? 'Incluso' : 'Non incluso'}</p></div>
                        {contract?.indirizzo_installazione && <div className="col-span-2"><p className="text-xs text-slate-400">Indirizzo Installazione</p><p className="font-medium">{contract.indirizzo_installazione}</p></div>}
                      </div>
                    </div>
                  )}
                  <div className="col-span-2 pt-4 border-t">
                    <p className="text-xs text-slate-500 mb-2">Pagamento e Bolletta</p>
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-xs text-slate-400">Metodo Pagamento</p>
                        <p className="font-medium">{contract?.metodo_pagamento === 'rid' ? 'RID Bancario' : contract?.metodo_pagamento === 'bollettino' ? 'Bollettino' : '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Tipo Bolletta</p>
                        <p className="font-medium">{contract?.tipo_bolletta === 'web' ? 'Bolletta Web' : contract?.tipo_bolletta === 'cartacea' ? 'Bolletta Cartacea' : '-'}</p>
                      </div>
                      {contract?.iban && (
                        <div>
                          <p className="text-xs text-slate-400">IBAN</p>
                          <p className="font-mono text-sm">{contract.iban}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {contract?.note_operative && (
                    <div className="col-span-2 pt-4 border-t">
                      <p className="text-xs text-slate-500 mb-1">Note Operative</p>
                      <p className="text-sm text-slate-700">{contract.note_operative}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client Info */}
          {client && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Cliente Associato
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                  onClick={() => navigate(`/clients/${client.id}`)}
                >
                  <div>
                    <p className="font-medium text-slate-900">
                      {client.tipo_cliente === 'business' && client.ragione_sociale 
                        ? client.ragione_sociale 
                        : `${client.nome} ${client.cognome}`}
                    </p>
                    <p className="text-sm text-slate-500">{client.telefono}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">{client.citta || ''} {client.provincia ? `(${client.provincia})` : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes and Activities */}
          <Tabs defaultValue="notes">
            <TabsList>
              <TabsTrigger value="notes">Note ({notes.length})</TabsTrigger>
              <TabsTrigger value="activities">Storico ({activities.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="notes" className="mt-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  {/* Input nuova nota */}
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        placeholder="Scrivi una nota... (anche il destinatario ricevera' una email)"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                        className="pr-10"
                        data-testid="note-input"
                      />
                    </div>
                    <Button onClick={handleAddNote} className="bg-sky-600 hover:bg-sky-700" data-testid="note-send-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 -mt-2 ml-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Una copia verra' inviata via email al destinatario
                  </p>
                  
                  {/* Conversazione note */}
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {notes.length === 0 ? (
                      <p className="text-center text-slate-400 py-8">Nessuna nota</p>
                    ) : (
                      notes.map((note) => {
                        const isMe = note.autore_id === currentUser?.id;
                        return (
                          <div key={note.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`note-${note.id}`}>
                            <div className={`max-w-[80%] ${isMe ? 'order-1' : 'order-1'}`}>
                              <div className={`rounded-2xl px-4 py-3 ${isMe 
                                ? 'bg-sky-600 text-white rounded-br-sm' 
                                : 'bg-slate-100 text-slate-900 rounded-bl-sm border border-slate-200'}`}>
                                {!isMe && (
                                  <p className={`text-xs font-semibold mb-1 ${isMe ? 'text-sky-100' : 'text-sky-700'}`}>
                                    {note.autore_nome}
                                  </p>
                                )}
                                <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isMe ? 'text-white' : 'text-slate-700'}`}>
                                  {note.contenuto}
                                </p>
                              </div>
                              <div className={`flex items-center gap-1.5 mt-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                {note.via_email && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                    <Mail className="w-2.5 h-2.5" /> via email
                                  </span>
                                )}
                                <span className="text-[10px] text-slate-400">{formatDate(note.created_at)}</span>
                                {isMe && <span className="text-[10px] text-slate-400">Tu</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="activities" className="mt-4">
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {activities.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nessuna attività</p>
                    ) : (
                      activities.map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3 p-3 border-l-2 border-slate-200">
                          <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{activity.azione}</p>
                            {activity.dettagli && (
                              <p className="text-sm text-slate-500">{activity.dettagli}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              {activity.utente_nome} • {formatDate(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Stato Pratica</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium ${getStatoColor(contract?.stato)}`}>
                  {STATI_PRATICA.find(s => s.value === contract?.stato)?.label}
                </span>
              </div>
              
              {/* Status indicator */}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50">
                {['attiva', 'accettata'].includes(contract?.stato) ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700">Pratica completata</span>
                  </>
                ) : ['scartata', 'annullata'].includes(contract?.stato) ? (
                  <>
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="text-sm font-medium text-red-700">Pratica non andata a buon fine</span>
                  </>
                ) : (
                  <>
                    <Clock className="w-5 h-5 text-amber-600" />
                    <span className="text-sm font-medium text-amber-700">In lavorazione</span>
                  </>
                )}
              </div>

              {/* Octopus Energy Trade Link Button */}
              {contract?.fornitore?.toLowerCase().includes('octopus') && (
                <div className="pt-2 border-t">
                  <Button 
                    className="w-full bg-[#ff00ff] hover:bg-[#e600e6] text-white"
                    onClick={() => window.open(OCTOPUS_TRADE_LINK, '_blank')}
                    data-testid="octopus-trade-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Inserisci su Octopus
                  </Button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Apre il portale Octopus per inserire il contratto
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Commission Card - hidden for interno and back_office */}
          {!hasRole(['interno', 'back_office']) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Euro className="w-5 h-5" />
                  Commissioni
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {hasRole(['sub_agent']) ? (
                  <>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">La Tua Commissione</p>
                      <p className={`text-xl font-bold ${contract?.commissione_maturata ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {formatCurrency(contract?.commissione_maturata)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Stato Pagamento</p>
                      {contract?.commissione_pagata ? (
                        <div>
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Pagato</span>
                          {contract?.note_pagamento && <p className="text-xs text-slate-500 mt-1">{contract.note_pagamento}</p>}
                        </div>
                      ) : contract?.commissione_maturata ? (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Da pagare</span>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Commissione Sub-Agente</p>
                      <p className={`text-xl font-bold ${contract?.commissione_maturata ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {formatCurrency(contract?.commissione_maturata)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Commissione Agenzia</p>
                      <p className={`text-xl font-bold ${contract?.commissione_agenzia ? 'text-sky-600' : 'text-slate-400'}`}>
                        {formatCurrency(contract?.commissione_agenzia)}
                      </p>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-slate-500 mb-1">Stato Pagamento Sub-Agente</p>
                      {contract?.commissione_pagata ? (
                        <div>
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Pagato</span>
                          {contract?.note_pagamento && <p className="text-xs text-slate-500 mt-1">{contract.note_pagamento}</p>}
                        </div>
                      ) : contract?.commissione_maturata ? (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Da pagare</span>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Dates Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Date</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Creata il</p>
                <p className="text-sm font-medium">{formatDate(contract?.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Ultimo aggiornamento</p>
                <p className="text-sm font-medium">{formatDate(contract?.updated_at)}</p>
              </div>
              {contract?.data_attivazione && (
                <div>
                  <p className="text-xs text-slate-500">Data Attivazione</p>
                  <p className="text-sm font-medium">{contract.data_attivazione}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContractDetail;
